// src/pages/Partners.jsx
import { useCallback, useEffect, useRef, useState } from 'react';
import './Subscriptions.scss';
import './Landing.scss';
import './Home.scss';
import './Partners.scss';
import '../components/ui/Form.scss';
import * as Yup from "yup";
import Heading from '../components/ui/Heading';
import clinics_logos from '/images/clinics-logos.png';
import benefits_step1 from '/icons/benefits-step-1.svg';
import benefits_step2 from '/icons/benefits-step-2.svg';
import benefits_step3 from '/icons/benefits-step-3.svg';
import upload from '/icons/upload.svg';
import clinics_landing from '/images/clinics-landing.png';
import dog from '/images/dog_upload-field.png';
import Button from '../components/ui/Button';
import "swiper/css/pagination";
import { useDispatch, useSelector } from 'react-redux';
import useFileHandlers from '../hooks/useFileHandlers';
import { resetCustomizer, selectUploadedImageIds, setCustomizerStep } from '../store/customizerSlice';
import { getImageFromDB, resetIndexedDB, saveImageToDB } from '../utils/indexedDB';
import { resetOrderDetails } from '../store/checkoutSlice';
import { useAffiliate } from '../hooks/useAffiliate';
import { Oval } from 'react-loader-spinner';
import FaqAccordion from '../sections/FaqAccordion';
import { ArrowRight } from 'lucide-react';
import { scrollToId } from '../utils/scrollToId';
import { useAppNavigate } from '../hooks/useAppNavigate';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { useAddPartnerRequestMutation } from '../store/apiSlice';

const faqs = [
  {
    question: 'How long does it take to be accepted as a TreatInk partner?',
    answer: 'A member of our team will review your application within 3 - 5 business days.',
  },
  {
    question: 'Can I close my partner page at any time?',
    answer: 'Yes, you can choose to close your partner account with us at any time.',
  },
  {
    question: 'How will I receive payments for my sales?',
    answer: 'You will be able to manage your payments from a partner dashboard and can receive them automatically over ACH on the payment schedule established with TreatInk.',
  },
  {
    question: 'How much will I receive per sale?',
    answer: 'TreatInk partners receive 20% of all sales revenue from their products.',
  },
];

function Partners({ isMobile }) {
  const dispatch = useDispatch();
  const navigate = useAppNavigate();
  const { handleFileSelect, handleDrop } = useFileHandlers();
  const cartItems = useSelector((state) => state.cart.cartItems);
  const { affiliate, isLoading } = useAffiliate();
  const colorMode = useSelector((state) => state.ui.colorMode);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [addPartnerRequest, { isLoading: isSubmitting, error, isSuccess }] = useAddPartnerRequestMutation();
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedImageId, setUploadedImageId] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  const validationSchema = Yup.object({
    affiliate_title: Yup.string().required('Company name is required'),
    contact_name: Yup.string().required('Full name is required'),
    contact_phone: Yup.string().required('Phone number is required'),
    contact_email: Yup.string().email('Invalid email').required('Email is required'),
    company_website: Yup.string().url('Must be a valid URL'),
    company_address: Yup.string(),
    company_about: Yup.string(),
  });

  const initialValues = {
    affiliate_title: '',
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    company_website: '',
    company_about: '',
    company_address: '',
    file: null,
  };

  //Reset customizer states
  const resetStates = () => {
    localStorage.removeItem("petType");
    localStorage.removeItem("petName");
    localStorage.removeItem("imageMetadata");
    localStorage.removeItem("label_template");
    resetIndexedDB("uploadedImages", cartItems);
    resetIndexedDB("resultImages", cartItems);
    resetIndexedDB("onProductImages", cartItems);
    dispatch(setCustomizerStep(1));
    dispatch(resetOrderDetails());
    dispatch(resetCustomizer());
  };

  useEffect(() => {
    resetStates();
  }, []);

  const handleFileInput = (file) => {
    if (
      file &&
      file.type.startsWith('image/') &&
      file.type !== 'image/svg+xml'
    ) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setPreviewImage(event.target.result);
        setUploadedFile(file);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (values, { resetForm }) => {
    if (!uploadedFile) {
      setSubmitStatus({ success: false, message: "Please upload your logo." });
      return;
    }

    const formData = new FormData();
    formData.append("affiliate_title", values.affiliate_title);
    formData.append("contact_name", values.contact_name);
    formData.append("contact_phone", values.contact_phone);
    formData.append("contact_email", values.contact_email);
    formData.append("company_website", values.company_website);
    formData.append("company_about", values.company_about);
    formData.append("company_address", values.company_address);
    formData.append("logo", uploadedFile);

    try {
      await addPartnerRequest(formData).unwrap();
      setSubmitStatus({
        success: true,
        message: "Thank you for applying to become a TreatInk partner! A team member will reach out regarding next steps soon.",
      });
      setUploadedFile(null);
      setPreviewImage(null);
      resetForm();
    } catch (err) {
      setSubmitStatus({
        success: false,
        message: err?.data?.error || "Submission failed. Please try again later.",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center w-full h-screen">
        <Oval
          visible={true}
          height={50}
          width={50}
          color="#a99cdf"
          strokeWidth="3"
          strokeWidthSecondary="3"
          secondaryColor="#e2e6ff"
          ariaLabel="oval-loading"
        />
      </div>
    );
  }

  return (
    <div className='landing-page partners-page'>
      <div className='home-hero full-width pt-5 md:pt-[50px]'>
        <div className='contained-width flex flex-col items-center'>
          <div className={`flex flex-col items-center gap-3 ${!affiliate ? 'md:gap-5' : ''} mt-0 md:mt-0`}>
            <div className='bg-primary-extra-light p-2 px-4 flex flex-row rounded-20 gap-2 max-w-fit' >
              <img src={clinics_logos} className="object-contain" alt="clinics logos" />
              <p className='mb-0'>and many others veterinary clinics have already tried</p>
            </div>

            {affiliate && (
              <div className='affiliate-logo-cont flex w-full justify-center my-5'>
                <img
                  src={affiliate.logo}
                  alt={`${affiliate.name} logo`}
                  className='affiliate-logo'
                />
              </div>
            )}

            <h1 className='color-swap text-center'>Launch Your Pet Shop<br />with TreatInk!</h1>
            <p className='color-swap text-mid text-center mb-5'>
              Choose your products and create your own branded personalization experience!
            </p>
            <Button
              type="button"
              variant="filled"
              style={{ maxWidth: 'fit-content' }}
              className='mb-10'
              onClick={() => {
                scrollToId('partners-form');
              }}
            >
              Become a Partner
            </Button>
          </div>
          <div className='image-container flex items-center justify-end'>
            <img src={clinics_landing} alt="" className="w-full max-w-none md:max-w-[750px]" />
          </div>
        </div>
      </div>

      <section className="benefits full-width mt-5 md:mt-0 flex justify-center">
        <div className="contained-width flex flex-col gap-10">
          <div className='flex flex-col md:flex-row gap-5'>
            <h3 className='color-swap w-[100%] md:w-[50%] text-center md:text-left'>The Benefits of<br />Choosing Treatink</h3>
            <p className='mb-0 color-swap w-[100%] md:w-[50%] pl-0 md:pl-10 text-center md:text-left'>Choosing to partner with Treatink means bringing your clients the highest-quality snacks and personalized service. We supply everything your clinic needs to keep pets healthy and satisfied.</p>
          </div>
          <div className='benefits-boxes flex justify-center gap-8 flex-col md:flex-row'>
            <div className='bg-white p-4 md:p-10 flex flex-col gap-4 box-shadow-md rounded-20 w-full'>
              <div className='benefits_step1 px-2 pr-4 flex gap-3 items-center max-w-fit rounded-[100px]'>
                <img src={benefits_step1} alt="" className='w-[40px] min-w-[40px] object-contain' />
                <p className='mb-0'>Customization</p>
              </div>
              <div className='flex flex-col gap-2'>
                <h5 className="form-title body-heading">Branded {!isMobile && <br />}Labels</h5>
                <p className="text-sm mb-0">Prominently feature your brand on any TreatInk product.</p>
              </div>
            </div>
            <div className='bg-white p-4 md:p-10 flex flex-col gap-4 box-shadow-md rounded-20 w-full'>
              <div className='benefits_step2 px-2 pr-4 flex gap-3 items-center max-w-fit rounded-[100px]'>
                <img src={benefits_step2} alt="" className='w-[40px] min-w-[40px] object-contain' />
                <p className='mb-0'>Quality</p>
              </div>
              <div className='flex flex-col gap-2'>
                <h5 className="form-title body-heading">Vet Approved {!isMobile && <br />}Products</h5>
                <p className="text-sm mb-0">Wholesome, safe ingredients - loved by pets, approved by vets.</p>
              </div>
            </div>
            <div className='bg-white p-4 md:p-10 flex flex-col gap-4 box-shadow-md rounded-20 w-full'>
              <div className='benefits_step3 px-2 pr-4 flex gap-3 items-center max-w-fit rounded-[100px]'>
                <img src={benefits_step3} alt="" className='w-[40px] min-w-[40px] object-contain' />
                <p className='mb-0'>Profit</p>
              </div>
              <div className='flex flex-col gap-2'>
                <h5 className="form-title body-heading">Recurring {!isMobile && <br />}Revenue</h5>
                <p className="text-sm mb-0">Unlock recurring revenue by offering your customers TreatInk subscriptions.</p>
              </div>
            </div>
          </div>
        </div>
      </section >

      <section className="why-us-container full-width flex justify-center pt-10 md:pt-5" id="how-it-works">
        <div className="contained-width flex flex-col gap-10 items-center">
          <Heading
            h4Text="It's easy!"
            h2Text="How It Works"
            h4Size="small"
            h2Size="small"
            align="center"
            h4Color="secondary"
          />
          <div className="card-boxes grid grid-cols-3 gap-5">
            <div className="card-box1 rounded-20 box-shadow-sm py-8 gap-8 flex flex-col items-center justify-end px-3 md:px-8">
              <div className="flex flex-col justiy-center items-center gap-1 md:gap-3">
                <h5 className="form-title body-heading text-center">Apply to Become <br />A Partner</h5>
                <p className="text-center mb-0">Tell us how your brand could benefit from being able to have a personalized shop offering TreatInk products.</p>
              </div>
            </div>
            <div className="card-box2 rounded-20 box-shadow-sm py-8 gap-8 flex flex-col items-center justify-end px-3 md:px-8">
              <div className="flex flex-col justiy-center items-center gap-1 md:gap-3">
                <h5 className="form-title body-heading text-center">Choose Your <br />Products</h5>
                <p className="text-center mb-0">Select which products from the TreatInk brand you would like to sell to your customers.</p>
              </div>
            </div>
            <div className="card-box3 rounded-20 box-shadow-sm py-8 gap-8 flex flex-col items-center justify-end px-3 md:px-8">
              <div className="flex flex-col justiy-center items-center gap-1 md:gap-3">
                <h5 className="form-title body-heading text-center">Customize <br />Your Shop</h5>
                <p className="text-center mb-0">Set your logo, upload a default label, and modify the design of your partner page to effectively drive brand affinity with your audience.</p>
              </div>
            </div>
          </div>
          <div className='flex justify-center'>
            <Button
              type="button"
              variant="filled"
              style={{ maxWidth: 'fit-content' }}
              onClick={() => {
                scrollToId('partners-form');
              }}
            >
              Get Started
            </Button>
          </div>
        </div>
      </section>

      <section className="partners-form full-width flex justify-center" id="partners-form">
        <div className="contained-width flex flex-col gap-10 items-center">
          <Heading
            h4Text="Signup Form"
            h2Text="Become a Partner"
            h4Size="small"
            h2Size="small"
            align="center"
            h4Color="secondary"
          />
          <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
            {({ isSubmitting, isValid, dirty }) => (
              <Form className="form-container flex gap-5" >

                <div className='flex flex-col md:flex-row gap-4'>
                  <div className="form-group w-full">
                    <label htmlFor="contact_name" className="text-mid">Your Full Name</label>
                    <Field type="text" name="contact_name" className="input-field" placeholder="Your Full Name" />
                    <ErrorMessage name="contact_name" component="div" className="error" />
                  </div>

                  <div className="form-group w-full">
                    <label htmlFor="contact_email" className="text-mid">Email</label>
                    <Field type="email" name="contact_email" className="input-field" placeholder="Email" />
                    <ErrorMessage name="contact_email" component="div" className="error" />
                  </div>
                </div>

                <div className='flex flex-col md:flex-row gap-4'>
                  <div className="form-group w-full">
                    <label htmlFor="contact_phone" className="text-mid">Phone Number</label>
                    <Field type="tel" name="contact_phone" className="input-field" placeholder="Phone Number" />
                    <ErrorMessage name="contact_phone" component="div" className="error" />
                  </div>

                  <div className="form-group w-full">
                    <label htmlFor="affiliate_title" className="text-mid">Company Name</label>
                    <Field type="text" name="affiliate_title" className="input-field" placeholder="Company Name" />
                    <ErrorMessage name="affiliate_title" component="div" className="error" />
                  </div>
                </div>

                <div className='flex flex-col md:flex-row gap-4'>
                  <div className="form-group w-full">
                    <label htmlFor="company_website" className="text-mid">Website (optional)</label>
                    <Field type="url" name="company_website" className="input-field" placeholder="Website (optional)" />
                    <ErrorMessage name="company_website" component="div" className="error" />
                  </div>

                  <div className="form-group w-full">
                    <label htmlFor="company_address" className="text-mid">Company Address (optional)</label>
                    <Field type="text" name="company_address" className="input-field" placeholder="Address (optional)" />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="company_about" className="text-mid">About Your Company (optional)</label>
                  <Field
                    as="textarea"
                    name="company_about"
                    className="input-field h-28 resize-none"
                    placeholder="Tell us more about you or your business"
                  />
                </div>

                {previewImage ?
                  <div className='flex justify-center'>
                    <div className="uploaded-image-preview relative">
                      <img
                        src={previewImage}
                        alt="Uploaded pet"
                        className="max-h-[200px] object-contain rounded-md"
                      />
                      <button
                        type="button"
                        className="absolute top-2 right-2 bg-secondary text-white rounded-full w-7 h-7 flex items-center justify-center text-xl"
                        onClick={async () => {
                          setPreviewImage(null);
                          setUploadedFile(null);
                          setUploadedImageId(null);
                        }}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                  :
                  <div
                    className="upload-area bg-white p-5 w-full border-3 border-dashed rounded-20 flex items-center justify-center cursor-pointer transition"
                    onClick={() => document.getElementById('fileInput').click()}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const file = e.dataTransfer.files[0];
                      handleFileInput(file);
                    }}
                    onDragOver={(e) => e.preventDefault()}
                  >
                    <div className="flex flex-col items-center text-center gap-3">
                      <img src={upload} alt="" />
                      <p>Upload your Logo</p>
                    </div>
                    <input
                      id="fileInput"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        handleFileInput(file);
                        e.target.value = '';
                      }}
                    />
                  </div>
                }

                <Button
                  type="submit"
                  variant="filled"
                  disabled={isSubmitting || !isValid || !dirty || !uploadedFile}
                  className="gap-4"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Request'} <ArrowRight size={20} />
                </Button>

                {submitStatus && (
                  <div className={`status-message text-center mt-2 ${submitStatus.success ? 'text-green-600' : 'text-red-500'}`}>
                    {submitStatus.message}
                  </div>
                )}
              </Form>
            )}
          </Formik>
        </div>
      </section>

      <section className="faq-container full-width flex justify-center pb-10 pt-10" id="faq">
        <div className="contained-width flex gap-0 md:gap-10 flex-col-reverse md:flex-row">
          <div className='image-block flex justify-center basis-[100%] md:basis-[30%] p-8'>
            <img src={dog} alt="" />
          </div>
          <div className='flex flex-col gap-5 md:gap-10 basis-[100%] md:basis-[70%]'>
            <Heading
              h4Text="FAQ"
              h2Text="You Ask, We Answer"
              h4Size="small"
              h2Size="small"
              align="center"
              h4Color="secondary"
            />
            <FaqAccordion faqs={faqs} />
          </div>
        </div>
      </section>

    </div >
  );
}

export default Partners;
